import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext.hook';
import { useToast } from '@/hooks/use-toast';
import { CURRENT_TERMS_VERSION, TERMS_CONTENT } from '@/constants/terms';
import { termsKeys } from '@/lib/query-keys';

async function computeHash(content: string): Promise<string> {
  const encoded = new TextEncoder().encode(content);
  const buffer = await crypto.subtle.digest('SHA-256', encoded);
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** Busca IP público com timeout de 3s para não travar o fluxo */
async function fetchPublicIP(): Promise<string> {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000);
    const res = await fetch('https://api.ipify.org?format=json', {
      signal: controller.signal,
    });
    clearTimeout(timeout);
    const data = await res.json();
    return data.ip ?? 'unknown';
  } catch {
    return 'unknown';
  }
}

interface TermsAcceptanceRecord {
  id: string;
  user_id: string;
  document_version: string;
  document_hash: string;
  cpf_cnpj: string;
  ip_address: string;
  user_agent: string;
  accepted_at: string;
  created_at: string;
}

/**
 * Phase 8 dynamic terms metadata (read-only surface; row inserted via
 * publish_terms_document RPC by admin/EF). null quando nenhuma versão dinâmica
 * foi publicada — fallback para CURRENT_TERMS_VERSION constant (legacy).
 */
interface CurrentTermsDocument {
  id: string;
  version_label: string;
  pdf_hash: string;
  pdf_storage_path: string;
}

export function useTermsAcceptance() {
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [documentHash, setDocumentHash] = useState<string | null>(null);

  // Pré-computar hash do documento legacy (fallback)
  useEffect(() => {
    computeHash(TERMS_CONTENT).then(setDocumentHash);
  }, []);

  // Phase 8: lookup da versão dinâmica vigente (se publicada).
  // Null result = nenhuma versão dinâmica ativa → fallback para legacy
  // CURRENT_TERMS_VERSION + TERMS_CONTENT hash.
  const { data: currentDoc, isLoading: loadingCurrentDoc } = useQuery<CurrentTermsDocument | null>({
    queryKey: termsKeys.currentDocument(),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('terms_documents')
        .select('id, version_label, pdf_hash, pdf_storage_path')
        .eq('is_current', true)
        .maybeSingle();
      if (error) throw error;
      return data ?? null;
    },
    // Phase 9 Plan 04 BUG-2 mitigation: investidor com sessao ativa precisa ver
    // o novo modal em <=10s apos admin publicar nova versao SEM reload manual.
    // Estrategia: refetchOnWindowFocus + staleTime: 30s + refetchInterval: 60s
    // (poll barato que cobre o caso "tab em foreground sem mudanca de focus").
    // Realtime subscription deferida (CONTEXT.md "v2").
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: 60 * 1000,
    retry: 2,
  });

  const effectiveVersion = currentDoc?.version_label ?? CURRENT_TERMS_VERSION;

  // Verificar se o usuário aceitou a versão atual dos termos
  const { data: acceptance, isLoading: checkingAcceptance } = useQuery({
    queryKey: termsKeys.acceptance(user?.id, effectiveVersion),
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from('terms_acceptances')
        .select('*')
        .eq('user_id', user.id)
        .eq('document_version', effectiveVersion)
        .order('accepted_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data as TermsAcceptanceRecord | null;
    },
    enabled: !!user?.id && !loadingCurrentDoc,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });

  const checking = loadingCurrentDoc || checkingAcceptance;
  const hasAccepted = !!acceptance;
  const needsAcceptance = !checking && !!user && !hasAccepted;

  // Mutation para registrar aceite
  const acceptMutation = useMutation({
    mutationFn: async (cpfCnpj: string) => {
      if (!user?.id) throw new Error('Usuário não autenticado');

      // Phase 8: hash dinâmico (currentDoc.pdf_hash) tem precedência sobre o
      // hash do TERMS_CONTENT legacy. Quando nenhuma versão dinâmica está
      // publicada, o hash legacy continua valendo.
      const hashToUse = currentDoc?.pdf_hash ?? documentHash;
      if (!hashToUse) throw new Error('Hash do documento não computado');

      const cleanDoc = cpfCnpj.replace(/\D/g, '');

      // Verificar se este CPF já aceitou esta versão dos termos (qualquer conta)
      const { data: alreadyAccepted, error: rpcError } = await supabase
        .rpc('check_cpf_terms_accepted', {
          p_cpf_cnpj: cleanDoc,
          p_document_version: effectiveVersion,
        });

      if (rpcError) throw rpcError;
      if (alreadyAccepted) {
        throw new Error('CPF_ALREADY_ACCEPTED');
      }

      // Buscar IP em paralelo para não bloquear (best-effort)
      const ip = await fetchPublicIP();

      const { data, error } = await supabase
        .from('terms_acceptances')
        .insert({
          user_id: user.id,
          document_version: effectiveVersion,
          document_hash: hashToUse,
          cpf_cnpj: cleanDoc,
          ip_address: ip,
          user_agent: navigator.userAgent,
          // Phase 8: vincula aceite à row dinâmica quando existir; legacy = null.
          terms_document_id: currentDoc?.id ?? null,
        })
        .select()
        .single();

      if (error) throw error;

      // Se o perfil não tem CPF/CNPJ, salvar no perfil também
      if (!profile?.cpf && (cleanDoc.length === 11 || cleanDoc.length === 14)) {
        await supabase
          .from('profiles')
          .update({ cpf: cleanDoc, updated_at: new Date().toISOString() })
          .eq('user_id', user.id);
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: termsKeys.acceptance(user?.id, effectiveVersion),
      });
      queryClient.invalidateQueries({
        queryKey: termsKeys.currentDocument(),
      });
      toast({
        title: 'Termos aceitos',
        description: 'Seus Termos de Uso foram registrados com sucesso.',
      });
    },
    onError: (error) => {
      console.error('Erro ao registrar aceite dos termos:', error);
      const errorMsg = (error as Error).message ?? '';
      const isCpfDuplicate = errorMsg === 'CPF_ALREADY_ACCEPTED'
        || errorMsg.includes('uq_terms_cpf_version');
      const isCnpjDuplicate = errorMsg.includes('uq_terms_cnpj_user_version');
      const isDuplicate = isCpfDuplicate || isCnpjDuplicate;

      toast({
        title: isDuplicate ? 'Documento já registrado' : 'Erro ao registrar aceite',
        description: isCpfDuplicate
          ? 'Este CPF já foi utilizado para aceitar os termos em outra conta. Entre em contato com o suporte.'
          : isCnpjDuplicate
            ? 'Você já aceitou os termos com este CNPJ.'
            : 'Tente novamente. Se o problema persistir, entre em contato com o suporte.',
        variant: 'destructive',
      });
    },
  });

  // Usa mutate (fire-and-forget) em vez de mutateAsync para evitar
  // unhandled promise rejection — o onError/onSuccess do mutation já cuida de tudo
  const acceptTerms = useCallback(
    (cpfCnpj: string): Promise<void> => {
      return new Promise<void>((resolve) => {
        acceptMutation.mutate(cpfCnpj, {
          onSettled: () => resolve(),
        });
      });
    },
    [acceptMutation]
  );

  return {
    hasAccepted,
    needsAcceptance,
    checking,
    accepting: acceptMutation.isPending,
    acceptTerms,
    acceptError: acceptMutation.error,
    currentVersion: effectiveVersion,
    // Phase 8: opcional — Phase 9 consumirá pdf_storage_path para signed URL +
    // react-pdf viewer. null quando nenhuma versão dinâmica foi publicada.
    currentDocument: currentDoc ?? null,
  };
}

export interface AdminTermsAcceptanceRow {
  id: string;
  user_id: string;
  document_version: string;
  document_hash: string;
  cpf_cnpj: string;
  ip_address: string;
  user_agent: string;
  accepted_at: string;
  created_at: string;
  /**
   * Phase 8 dynamic terms link. NULL para aceites legacy (texto v1, antes da
   * migracao para terms_documents). Phase 9 Plan 04 surfaces este campo para
   * AdminTermsAcceptances renderizar badge "Texto legado" vs version_label.
   */
  terms_document_id: string | null;
  // Dados do perfil (join manual)
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}

/**
 * Hook para admin: buscar todos os aceites de termos com dados do perfil.
 *
 * Phase 9 Plan 04: opcional `termsDocumentId` filter para a tab Aceites
 * filtrar por versao especifica (consumido via `?document=<uuid>` query
 * param da rota /admin/termos).
 */
export function useAdminTermsAcceptances(
  filters?: { userId?: string; termsDocumentId?: string },
) {
  return useQuery<AdminTermsAcceptanceRow[]>({
    queryKey: termsKeys.adminAcceptances(filters),
    queryFn: async () => {
      // Buscar aceites
      let query = supabase
        .from('terms_acceptances')
        .select('*')
        .order('accepted_at', { ascending: false });

      if (filters?.userId) {
        query = query.eq('user_id', filters.userId);
      }
      if (filters?.termsDocumentId) {
        query = query.eq('terms_document_id', filters.termsDocumentId);
      }

      const { data: acceptances, error } = await query;
      if (error) throw error;
      if (!acceptances?.length) return [];

      // Buscar perfis dos user_ids únicos
      const userIds = [...new Set(acceptances.map((a) => a.user_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, first_name, last_name, email')
        .in('user_id', userIds);

      const profileMap = new Map(
        (profiles ?? []).map((p) => [p.user_id, p])
      );

      return acceptances.map((a) => {
        const profile = profileMap.get(a.user_id);
        return {
          ...a,
          first_name: profile?.first_name ?? null,
          last_name: profile?.last_name ?? null,
          email: profile?.email ?? null,
        };
      });
    },
  });
}
